import { Link, Navigate } from "react-router-dom";

import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/lib/auth";

import "@/styles/tokens-v2-components.css";
import "./Home.css";

/**
 * Public marketing homepage. Faithful port of the prototype at
 * `New UI Handoff/screens/home-v3.html` (canonical per
 * `New UI Handoff/handoff/SCREENS.md` § "/ — Marketing home").
 *
 * Authenticated users are redirected straight into the app at
 * /dashboard — the marketing surface only renders for anonymous
 * visitors. Sign-out clears the session and they can come back here.
 *
 * The page is composed as one component per section; each section's
 * markup mirrors the prototype 1:1, including copy. The only deltas
 * from the prototype are:
 *   - Brand wordmark uses the shared <Wordmark /> component instead
 *     of the prototype's inline `<span class="brand">…</span>` markup.
 *   - Anchor `href="auth.html"` links are routed via React Router to
 *     `/login` (Sign in) and `/register` (Start free / trial CTAs).
 *   - In-page anchors (#product, #chains, #pricing, #docs,
 *     #changelog) stay as fragment links — those targets exist as
 *     section ids on this page or are placeholders for future routes.
 */
export default function Home() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <main className="home-page">
      <HomeNav />
      <HomeHero />
      <HomeMarquee />
      <HomeLogoCloud />
      <HomeThreeLayer />
      <HomeWorkhorse />
      <HomeWatchOnly />
      <HomePricing />
      <HomeTestimonial />
      <HomeFinalCta />
      <HomeFooter />
    </main>
  );
}

/* ----------------------------- NAV ----------------------------- */

function HomeNav() {
  return (
    <header className="home-nav">
      <div className="home-nav-inner">
        <Link
          to="/"
          aria-label="Cawamba home"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Wordmark size="md" />
        </Link>
        <nav className="home-nav-links" aria-label="Marketing">
          <a href="#product">Product</a>
          <a href="#chains">Chains</a>
          <a href="#pricing">Pricing</a>
          <a href="#docs">Docs</a>
          <a href="#changelog">Changelog</a>
        </nav>
        <div className="home-nav-cta">
          <Link to="/login" className="home-btn">Sign in</Link>
          <Link to="/register" className="home-btn home-btn-primary">Start free</Link>
        </div>
      </div>
    </header>
  );
}

/* ----------------------------- HERO ---------------------------- */

function HomeHero() {
  return (
    <section className="home-hero">
      <div className="home-hero-inner">
        <div className="home-hero-eyebrow">
          <span className="home-hero-pulse" aria-hidden />
          Watch-only · 14 chains · SOC 2 Type II
        </div>
        <h1>
          Crypto bookkeeping <em className="home-hero-em">your auditor</em>{" "}
          will sign off on.
        </h1>
        <p className="home-hero-sub">
          Cawamba is the read-only ledger for finance teams who&apos;d rather
          close the books than reconcile receipts. Sync every wallet, then add
          the context block-explorers don&apos;t store — comments, tags,
          owners — at the wallet, holding, and transaction level.
        </p>
        <div className="home-hero-cta">
          <Link to="/register" className="home-btn home-btn-primary home-btn-lg">
            Start free — no card →
          </Link>
          <a href="#product" className="home-btn home-btn-lg">See it move</a>
        </div>
        <div className="home-hero-meta">
          <span>// 1 wallet free</span>
          <span className="home-meta-dot" aria-hidden>·</span>
          <span>$24 / wallet / mo after</span>
          <span className="home-meta-dot" aria-hidden>·</span>
          <span>Cancel anytime</span>
          <span className="home-meta-dot" aria-hidden>·</span>
          <span>No keys, ever</span>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- MARQUEE --------------------------- */

/**
 * Chain strip. The track is rendered twice in DOM so the
 * `translateX(-100%)` keyframe loops seamlessly. `aria-hidden` because
 * it's decorative — assistive tech reads chain support from the
 * Workhorse "14 chains" metric below.
 */
function HomeMarquee() {
  const chains = [
    "BTC", "ETH", "SOL", "TRON", "BASE", "ARBITRUM", "OPTIMISM",
    "POLYGON", "AVALANCHE", "BNB", "XRP", "USDT-ERC20", "USDT-TRC20",
    "USDC-ETH", "USDC-BASE",
  ];
  const renderRun = (keyPrefix: string) =>
    chains.map((c, i) => (
      <span key={`${keyPrefix}-${c}-${i}`}>
        <span>{c}</span>
        <span className="home-marquee-sep" aria-hidden>/</span>
      </span>
    ));
  return (
    <div className="home-marquee" aria-hidden>
      <div className="home-marquee-track">
        {renderRun("a")}
        {renderRun("b")}
      </div>
    </div>
  );
}

/* -------------------------- LOGO CLOUD ------------------------- */

function HomeLogoCloud() {
  return (
    <section className="home-cloud">
      <div className="home-cloud-wrap">
        <div className="home-cloud-label">Quietly running the books at</div>
        <div className="home-cloud-logos">
          <div className="home-cloud-logo">Halverson&nbsp;&amp;&nbsp;Co</div>
          <div className="home-cloud-logo b">NORTHWIND</div>
          <div className="home-cloud-logo c">parsec.fi</div>
          <div className="home-cloud-logo b">RIVET&nbsp;CAPITAL</div>
          <div className="home-cloud-logo">Atelier Treasury</div>
          <div className="home-cloud-logo c">brick / labs</div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- THREE-LAYER ------------------------- */

function HomeThreeLayer() {
  return (
    <section className="home-three" id="product">
      <div className="home-three-wrap">
        <div className="home-section-eyebrow">// 03 levels of context</div>
        <h2>
          Your data lives at three layers —{" "}
          <em>so does our metadata.</em>
        </h2>
        <p className="home-three-lead">
          Other tools tag transactions and stop. Cawamba tags the wallet, the
          holding (token-on-chain), and the transaction — because USDT-ERC20
          in your hot wallet is a different story from USDT-TRC20 in your
          customer-funds wallet, and your accountant needs to see both.
        </p>

        <div className="home-three-grid">
          <article className="home-layer l1">
            <div className="home-layer-num" aria-hidden>01</div>
            <div className="home-layer-who">// Wallet</div>
            <h3>Whose money, what role.</h3>
            <p>
              Group addresses into wallet folders. Tag the role — treasury,
              payroll, customer-funds — once at the top, inherit everywhere
              underneath.
            </p>
            <div className="home-layer-demo">
              <div className="home-row"><span className="k">name</span><span className="v">Treasury · Hot</span></div>
              <div className="home-row"><span className="k">addresses</span><span className="v">3</span></div>
              <div className="home-row"><span className="k">value</span><span className="v">$724,193.40</span></div>
              <div className="home-tags">
                <span className="home-tag indigo">treasury</span>
                <span className="home-tag">operating</span>
                <span className="home-tag">multi-sig 3/5</span>
              </div>
            </div>
          </article>

          <article className="home-layer l2">
            <div className="home-layer-num" aria-hidden>02</div>
            <div className="home-layer-who">// Holding</div>
            <h3>Token plus chain plus context.</h3>
            <p>
              One row per token-on-chain. Same USDT, different chains,
              different stories. Tag separately so your reports don&apos;t lie
              about where liquidity actually lives.
            </p>
            <div className="home-layer-demo">
              <div className="home-row"><span className="k">token</span><span className="v">USDC</span></div>
              <div className="home-row"><span className="k">chain</span><span className="v">Ethereum · ERC-20</span></div>
              <div className="home-row"><span className="k">balance</span><span className="v">524,193.40</span></div>
              <div className="home-tags">
                <span className="home-tag coral">customer-funds</span>
                <span className="home-tag">stripe-payouts</span>
              </div>
            </div>
          </article>

          <article className="home-layer l3">
            <div className="home-layer-num" aria-hidden>03</div>
            <div className="home-layer-who">// Transaction</div>
            <h3>The line your auditor reads.</h3>
            <p>
              Comments, tags, suggested labels. The level where your
              accountant lives. Bulk-tag a thousand rows, then never
              reconstruct from memory again.
            </p>
            <div className="home-layer-demo">
              <div className="home-row"><span className="k">hash</span><span className="v">0x7a2f…42b9</span></div>
              <div className="home-row"><span className="k">amount</span><span className="v">+ 12,500.00 USDC</span></div>
              <div className="home-row"><span className="k">memo</span><span className="v">&quot;Stripe payout, Apr&quot;</span></div>
              <div className="home-tags">
                <span className="home-tag lavender">customer-payment</span>
                <span className="home-tag">stripe-payout</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

/* -------------------------- WORKHORSE -------------------------- */

function HomeWorkhorse() {
  return (
    <section className="home-workhorse" id="chains">
      <div className="home-workhorse-wrap">
        <div className="home-section-eyebrow">// The workhorse view</div>
        <h2>
          Spreadsheet ergonomics, <em>watch-only safety.</em>
        </h2>
        <p className="home-workhorse-lead">
          Bulk-tag, mark spam, re-assign owners, accept ML suggestions,
          export — all without ever touching a private key. Built for the
          analyst staring at 8,000 rows on Tuesday.
        </p>

        <div className="home-tx-mock">
          <div className="home-callout-pill">↑ live workhorse view</div>
          <div className="home-tx-mock-head">
            <div className="home-tx-filt on">Grouped</div>
            <div className="home-tx-filt">Raw</div>
            <div className="home-tx-filt">Last 30d</div>
            <div className="home-tx-filt">All wallets</div>
            <div className="home-tx-filt">All chains</div>
            <div className="home-tx-filt">Needs context · 14</div>
            <div className="home-tx-right">3 selected</div>
          </div>
          <table>
            <thead>
              <tr>
                <th><span className="sr-only">Selection</span></th>
                <th>Date</th>
                <th>Transaction</th>
                <th>Wallet</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th>Context</th>
              </tr>
            </thead>
            <tbody>
              <tr className="hi">
                <td>✓</td>
                <td><div className="date">Apr 25, 14:02</div></td>
                <td>
                  <div className="what">
                    <div className="arr in" aria-hidden>↓</div>
                    <div>
                      <div className="lbl">Inbound transfer · USDC</div>
                      <div className="sub">tx 0x7a2f…42b9</div>
                    </div>
                  </div>
                </td>
                <td>Treasury · Hot</td>
                <td className="amt pos">+ 12,500.00</td>
                <td>
                  <div className="ctx">
                    <span className="t lav">customer-payment</span>
                    <span className="t">stripe-payout</span>
                  </div>
                </td>
              </tr>
              <tr className="hi">
                <td>✓</td>
                <td><div className="date">Apr 25, 11:24</div></td>
                <td>
                  <div className="what">
                    <div className="arr out" aria-hidden>↑</div>
                    <div>
                      <div className="lbl">Outbound transfer · ETH</div>
                      <div className="sub">to 0x88f1…02e4</div>
                    </div>
                  </div>
                </td>
                <td>Operations</td>
                <td className="amt neg">− 4.2105</td>
                <td>
                  <div className="ctx">
                    <span className="t cor">vendor-payment</span>
                    <span className="t">aws-q2</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td />
                <td><div className="date">Apr 25, 09:08</div></td>
                <td>
                  <div className="what">
                    <div className="arr in" aria-hidden>↓</div>
                    <div>
                      <div className="lbl">Contract call · Uniswap v3</div>
                      <div className="sub">swap USDC → ETH</div>
                    </div>
                  </div>
                </td>
                <td>Treasury · Hot</td>
                <td className="amt pos">+ 1.2003</td>
                <td>
                  <div className="ctx">
                    <span className="t chart">hedge</span>
                    <span className="t empty">+ tag</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td />
                <td><div className="date">Apr 24, 22:16</div></td>
                <td>
                  <div className="what">
                    <div className="arr out" aria-hidden>↑</div>
                    <div>
                      <div className="lbl">Outbound · USDC</div>
                      <div className="sub">payroll batch · 14 rows</div>
                    </div>
                  </div>
                </td>
                <td>Payroll</td>
                <td className="amt neg">− 84,200.00</td>
                <td>
                  <div className="ctx">
                    <span className="t lav">payroll</span>
                    <span className="t">apr-2025</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td />
                <td><div className="date">Apr 24, 18:03</div></td>
                <td>
                  <div className="what">
                    <div className="arr in" aria-hidden>↓</div>
                    <div>
                      <div className="lbl">Inbound · BTC</div>
                      <div className="sub">from cold wallet</div>
                    </div>
                  </div>
                </td>
                <td>Treasury · Cold</td>
                <td className="amt pos">+ 0.5821</td>
                <td>
                  <div className="ctx">
                    <span className="t empty">needs context</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="home-metrics-row">
          <div className="home-metric">
            <div className="v chart">14</div>
            <div className="lbl">Chains supported</div>
          </div>
          <div className="home-metric">
            <div className="v lav">~3.2s</div>
            <div className="lbl">Median sync per wallet</div>
          </div>
          <div className="home-metric">
            <div className="v cor">0</div>
            <div className="lbl">Private keys we hold</div>
          </div>
          <div className="home-metric">
            <div className="v mint">98.4%</div>
            <div className="lbl">ML auto-categorize accuracy</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- WATCH-ONLY -------------------------- */

function HomeWatchOnly() {
  return (
    <section className="home-watch">
      <div className="home-watch-wrap">
        <div>
          <div
            className="home-section-eyebrow"
            style={{ color: "var(--indigo)" }}
          >
            // Safety
          </div>
          <h2>
            Watch-only by design. <em>Audit-ready</em> by default.
          </h2>
          <p>
            Cawamba never asks for keys, never custodies funds, never signs a
            transaction. Read-only addresses only — connect a wallet by
            pasting an address. Your security team will yawn through the
            review.
          </p>
          <div className="home-watch-stamps">
            <div className="home-stamp">SOC 2 Type II</div>
            <div className="home-stamp">No key custody</div>
            <div className="home-stamp">No signing</div>
            <div className="home-stamp">Read-only RPC</div>
          </div>
        </div>
        <div className="home-watch-art">
          <div className="home-padlock-card">
            <div className="key">// connecting wallet</div>
            <div className="val">
              0x7a2f8c91b4d7e3a2c5f8b9e6d4a3c7f2…42b9
            </div>
            <div className="key">// permission requested</div>
            <div className="val mint">read-only · balances + history</div>
            <div className="key">// permission denied</div>
            <div className="val coral">spend · sign · approve</div>
            <div className="home-stamp-row">
              <span className="badge">✓ added</span>
              <span className="home-padlock-note">
                no transaction was signed
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- PRICING --------------------------- */

function HomePricing() {
  return (
    <section className="home-pricing" id="pricing">
      <div className="home-pricing-wrap">
        <div className="home-section-eyebrow">// Pricing</div>
        <h2>
          One wallet free. <em>Then, plain pricing.</em>
        </h2>

        <div className="home-tiers">
          <div className="home-tier">
            <div className="name">// Solo</div>
            <div className="price">
              $0<small>/forever</small>
            </div>
            <div className="desc">
              For founders and operators tracking one wallet by themselves.
            </div>
            <ul>
              <li>1 wallet</li>
              <li>14 chains</li>
              <li>Comments + tags</li>
              <li>CSV export</li>
            </ul>
            <Link to="/register" className="cta-btn">Start free</Link>
          </div>
          <div className="home-tier featured">
            <div className="badge">Most teams</div>
            <div className="name">// Team</div>
            <div className="price">
              $24<small>/wallet/mo</small>
            </div>
            <div className="desc">
              For finance teams closing the books every month, on time.
            </div>
            <ul>
              <li>Unlimited wallets</li>
              <li>Tag library + bulk actions</li>
              <li>Sync jobs + alerts</li>
              <li>Quickbooks / Xero export</li>
              <li>Up to 10 seats</li>
            </ul>
            <Link to="/register" className="cta-btn">Start 30-day trial</Link>
          </div>
          <div className="home-tier">
            <div className="name">// Platform</div>
            <div className="price">Custom</div>
            <div className="desc">
              For platforms watching customer wallets at scale, with SSO +
              audit.
            </div>
            <ul>
              <li>White-label workspace</li>
              <li>API access</li>
              <li>SSO + SCIM</li>
              <li>SOC 2 audit support</li>
              <li>Unlimited seats</li>
            </ul>
            <a href="#contact" className="cta-btn">Talk to us</a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- TESTIMONIAL ------------------------- */

function HomeTestimonial() {
  return (
    <section className="home-testimonial">
      <div className="home-testimonial-wrap">
        <blockquote>
          &quot;We <em>stopped reconstructing</em> transactions from Slack
          threads in month one. Our close went from <em>nine days</em> to{" "}
          <em>three.</em>&quot;
        </blockquote>
        <div className="home-attrib">
          <div className="av" aria-hidden>M</div>
          <div>
            <div className="home-attrib-name">Maya Tanaka</div>
            <div>Controller · Northwind Capital</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------- FINAL CTA -------------------------- */

function HomeFinalCta() {
  return (
    <section className="home-cta-final">
      <h2>
        Stop reconstructing transactions <em>from memory.</em>
      </h2>
      <p className="sub">
        Add Cawamba next to your accounting stack. Free for the first wallet,
        $24/wallet/month after. Cancel anytime.
      </p>
      <div className="row">
        <Link to="/register" className="home-btn home-btn-primary home-btn-lg">
          Start free — no card →
        </Link>
        <a href="#contact" className="home-btn home-btn-lg">Book a demo</a>
      </div>
    </section>
  );
}

/* ---------------------------- FOOTER --------------------------- */

function HomeFooter() {
  return (
    <footer className="home-footer">
      <div className="home-footer-inner">
        <div className="home-footer-brand">
          <Wordmark size="md" onDark />
          <p>
            Watch-only crypto bookkeeping for finance teams. Built in Tel
            Aviv &amp; Lisbon.
          </p>
        </div>
        <div>
          <h5>Product</h5>
          <ul>
            <li><a href="#wallets">Wallets</a></li>
            <li><a href="#transactions">Transactions</a></li>
            <li><a href="#sync">Sync jobs</a></li>
            <li><a href="#pricing">Pricing</a></li>
          </ul>
        </div>
        <div>
          <h5>Resources</h5>
          <ul>
            <li><a href="#docs">Docs</a></li>
            <li><a href="#chains">Chain support</a></li>
            <li><a href="#changelog">Changelog</a></li>
            <li><a href="#status">Status</a></li>
          </ul>
        </div>
        <div>
          <h5>Company</h5>
          <ul>
            <li><a href="#about">About</a></li>
            <li><a href="#security">Security</a></li>
            <li><a href="#press">Press</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
      </div>
      <div className="legal">
        <span>// © 2026 Cawamba — watch-only — SOC 2 Type II</span>
        <span>privacy · terms · security · status</span>
      </div>
    </footer>
  );
}
